// Window event the bulk-action flows dispatch and the Notifications nav item
// listens for to show its hint. The hint copy travels in `detail`. Shared so the
// dispatching roots and the nav item (a separate React root) agree on the name.
export const NOTIFICATION_HINT_REQUESTED_EVENT = "notification-hint:requested"

const DEFAULT_HINT_DESCRIPTION =
  "You can review the progress in the Notifications menu."

// sessionStorage key holding a hint requested right before a page reload, so it
// can be surfaced once the nav remounts. Only the reloading path writes it; the
// nav item clears it once the hint actually appears. Namespaced to avoid
// collisions.
const PENDING_HINT_STORAGE_KEY = "notification-hint:pending"

// An action left a result in the notification drawer, so point a hint at the
// Notifications item to show where it went.
//
//   - Default: dispatch an event the mounted Notifications item shows now.
//   - willReload: the caller reloads on completion (CSV/provider imports), so a
//     dispatch would flash the hint then get wiped. Stash it instead; the nav
//     item recovers it on its next mount and shows it once.
export function notifyDrawerAction({
  title,
  description = DEFAULT_HINT_DESCRIPTION,
  willReload = false
}) {
  const detail = { title, description }

  if (willReload) {
    stashPendingDrawerHint(detail)
    return
  }

  // Tell the nav to refetch so a freshly created topic mounts the Notifications
  // item, then point the hint at it. Mirrors the refresh the bulk modal already
  // sends; a second dispatch there is harmless.
  window.dispatchEvent(
    new CustomEvent("notifications:refresh", { bubbles: true })
  )
  window.dispatchEvent(
    new CustomEvent(NOTIFICATION_HINT_REQUESTED_EVENT, { detail })
  )
}

function stashPendingDrawerHint(detail) {
  try {
    window.sessionStorage.setItem(
      PENDING_HINT_STORAGE_KEY,
      JSON.stringify(detail)
    )
  } catch {
    // sessionStorage can throw (private mode, quota). The hint is a
    // non-critical nicety, so swallow it rather than break the caller.
  }
}

// Reads (without clearing) a hint stashed for after a reload. The nav item calls
// this on mount to recover it; it clears the stash via clearPendingDrawerHint
// once the hint has shown, not on read, so an unmounted item doesn't drop it.
export function readPendingDrawerHint() {
  try {
    const raw = window.sessionStorage.getItem(PENDING_HINT_STORAGE_KEY)
    return raw ? JSON.parse(raw) : null
  } catch {
    return null
  }
}

export function clearPendingDrawerHint() {
  try {
    window.sessionStorage.removeItem(PENDING_HINT_STORAGE_KEY)
  } catch {
    // Nothing to recover from; the stash is best-effort.
  }
}
